import { GenerationModelDetails } from '~/domain/dicom/dicomBase';
import { Modality } from '~/domain/dicom/modality';
import { Image3D } from '~/domain/patient/image3d';

export type ImageMetadata = {
    frameOfReferenceUid: string;
    id: string;
    modality: Modality;
    rows: number;
    cols: number;
    length: number;
    series: {
        description: string;
        date: Date | undefined;
        id: string;
    };
    manufacturer: string;
    manufacturerModelName: string;
    imageType: string[];
    modalitySpecificAttributes: {
        kvp?: string;
        ctdi?: string;
        kernel?: string;
        scanningSequence?: string;
        mraAcquisitionType?: string;
        sequenceVariant?: string;
        protocolName?: string;
    };
    pixelSpacing: number[];
    fieldOfViewMm: [number, number, number];
    imageOrientation: number[][];
    patientPosition?: string;
    stationName?: string;
    filterType?: string;
    generationModel?: GenerationModelDetails;
    hasInterpolatedSlices: boolean;
    interpolatedSliceCount: number;
};

export const transform3DImageData = (image: Image3D): ImageMetadata => {
    const {
        cols,
        rows,
        imageType,
        kvp,
        ctdi,
        kernel,
        scanningSequence,
        mraAcquisitionType,
        sequenceVariant,
        protocolName,
        patientPosition,
        stationName,
        filterType,
    } = image.firstSlice;
    const { seriesDescription, seriesDate, modality } = image.seriesMetadata;

    return {
        frameOfReferenceUid: image.frameOfReferenceUid,
        id: image.seriesUid,
        modality: modality ?? Modality.UNKNOWN,
        rows,
        cols,
        length: image.length,
        series: {
            description: seriesDescription,
            date: seriesDate,
            id: image.seriesUid,
        },
        manufacturer: image.firstSlice.manufacturer,
        manufacturerModelName: image.firstSlice.manufacturerModelName,
        imageType: imageType,
        modalitySpecificAttributes: {
            kvp,
            ctdi,
            kernel,
            scanningSequence,
            mraAcquisitionType,
            sequenceVariant,
            protocolName,
        },
        pixelSpacing: Array.from(image.grid.getRes()),
        fieldOfViewMm: [
            image.grid.sizeXMM,
            image.grid.sizeYMM,
            image.grid.sizeZMM,
        ],
        imageOrientation: [
            Array.from(image.grid.getUnitX()),
            Array.from(image.grid.getUnitY()),
            Array.from(image.grid.getUnitZ()),
        ],
        patientPosition,
        stationName,
        filterType,
        generationModel: image.generationModel,
        hasInterpolatedSlices: image.hasInterpolatedSlices,
        interpolatedSliceCount: image.interpolatedSliceCount,
    };
};
